import { useEffect } from "react";
import {
  Activity,
  ArrowRight,
  BarChart3,
  Brain,
  CheckCircle2,
  Code2,
  Compass,
  Factory,
  Focus,
  Gamepad2,
  GitBranch,
  Layers3,
  Lightbulb,
  Link2,
  LogIn,
  PackageCheck,
  Rocket,
  Search,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Target,
} from "lucide-react";
import { Link } from "react-router-dom";
import { CapabilityStatus, CAPABILITY_STATUS } from "../components/CapabilityStatus";
import "./PublicHomePage.css";
import "./PublicLifecycle.css";

const arms = [
  {
    icon: Focus,
    tone: "cyan",
    title: "FocusLinc",
    kicker: "ATTENTION",
    copy: "The daily front door. It ranks what needs human attention, shows what is waiting or at risk, and gives you an AI operating partner grounded in live work.",
    tags: ["Top 3", "Attention Engine", "AI partner", "Quick capture"],
  },
  {
    icon: Brain,
    tone: "purple",
    title: "CommandLinc",
    kicker: "AI OPERATIONS",
    copy: "The governed operating system underneath LincWerks — moving work from idea through discovery, validation, decisions, BuildLinc, review, QA, release, client support and continuous improvement.",
    tags: ["Lifecycle", "Missions", "BuildLinc + QA", "Audit + analytics"],
  },
  {
    icon: Gamepad2,
    tone: "blue",
    title: "GameLinc",
    kicker: "SPORTS PLATFORM",
    copy: "A sports operating platform built around Work · Play · Connect, combining team and league operations with communication, competition, immersive spaces and community.",
    tags: ["Teams + events", "Competition", "Communication", "Immersive sports"],
  },
  {
    icon: ShoppingBag,
    tone: "amber",
    title: "GearLinc",
    kicker: "STOREFRONT + COMMERCE",
    copy: "The customer-facing merchandise layer. Store-ready products arrive from ForgeLinc; GearLinc handles catalog, merchandising, pricing, selling, orders and the customer experience.",
    tags: ["Storefront", "Catalog + pricing", "Orders", "Customer experience"],
  },
  {
    icon: Factory,
    tone: "teal",
    title: "ForgeLinc",
    kicker: "DEVELOPMENT + PRODUCTION",
    copy: "The physical-product engine: concept, design, prototype, production recipe, materials, manufacturing and QA — ending with a Ready for Store handoff into GearLinc.",
    tags: ["Product development", "Prototyping", "Production", "QA + handoff"],
  },
];

const lifecycle = [
  ["Idea", Lightbulb, "purple"],
  ["Discover", Search, "blue"],
  ["Validate", Target, "cyan"],
  ["Decide", Compass, "violet"],
  ["BuildLinc", Code2, "teal"],
  ["Review + Revise", GitBranch, "amber"],
  ["QA + Security", ShieldCheck, "green"],
  ["Release", Rocket, "violet"],
  ["Operate + Support", Activity, "slate"],
  ["Learn + Improve", BarChart3, "purple"],
];

const capabilityOrder = ["live", "workflow_ready", "not_connected", "in_development", "planned"];

export default function PublicHomePage() {
  useEffect(() => {
    const previous = document.title;
    document.title = "LincWerks · Build, Operate, Connect";
    return () => { document.title = previous; };
  }, []);

  return (
    <div className="public-home" data-testid="public-home" data-edit-name="Public home">
      <header className="public-nav">
        <Link className="public-brand" to="/" aria-label="LincWerks home">
          <img className="public-brand-logo" src="/LincWerks2026Logo.png" alt="LincWerks" />
          <span className="public-wordmark"><span>Linc</span>Werks</span>
        </Link>
        <nav className="public-nav-links" aria-label="Public navigation">
          <a href="#ecosystem">Ecosystem</a><a href="#lifecycle">How it works</a><a href="#purpose">Purpose</a>
        </nav>
        <Link className="public-login" to="/login"><LogIn size={15} /> Sign in</Link>
      </header>

      <main className="public-main">
        <section className="public-hero" data-edit-name="Public hero">
          <div className="public-hero-copy">
            <div className="public-kicker"><span /> ONE CONNECTED OPERATING ECOSYSTEM</div>
            <h1>Turn ideas into businesses, products, experiences and action.</h1>
            <p>LincWerks is the parent operating ecosystem connecting AI-assisted development, business operations, sports technology, commerce and physical production. The goal is simple: reduce the distance between an idea and something that actually works.</p>
            <div className="public-hero-actions">
              <Link className="public-primary" to="/login">Enter LincWerks <ArrowRight size={16} /></Link>
              <a className="public-secondary" href="#ecosystem">See the ecosystem</a>
            </div>
            <div className="public-hero-proof">
              <span><CheckCircle2 size={13} /> Human decisions stay gated</span>
              <span><CheckCircle2 size={13} /> AI work stays traceable</span>
              <span><CheckCircle2 size={13} /> Digital + physical work connect</span>
            </div>
          </div>

          <div className="public-system-map" aria-label="LincWerks ecosystem map">
            <div className="public-map-core"><img src="/LincWerks2026Logo.png" alt="" /><b>LincWerks</b><small>Operating ecosystem</small></div>
            <div className="public-map-orbit orbit-a"><Focus size={18} /><span>FocusLinc</span></div>
            <div className="public-map-orbit orbit-b"><Brain size={18} /><span>CommandLinc</span></div>
            <div className="public-map-orbit orbit-c"><Gamepad2 size={18} /><span>GameLinc</span></div>
            <div className="public-map-orbit orbit-d"><ShoppingBag size={18} /><span>GearLinc</span></div>
            <div className="public-map-orbit orbit-e"><Factory size={18} /><span>ForgeLinc</span></div>
            <div className="public-map-ring ring-one" /><div className="public-map-ring ring-two" />
          </div>
        </section>

        <section id="ecosystem" className="public-section">
          <div className="public-section-head"><small>THE LINCWERKS FAMILY</small><h2>Different arms. One connected model.</h2><p>Each Linc has a focused job, but the value comes from letting information and work move between them instead of living in separate silos. For physical products, ForgeLinc develops and makes the product; GearLinc turns the approved result into the storefront offer and customer experience.</p></div>
          <div className="public-arm-grid">
            {arms.map(({ icon: Icon, tone, title, kicker, copy, tags }) => (
              <article className={`public-arm tone-${tone}`} key={title}>
                <header><span><Icon size={20} /></span><div><small>{kicker}</small><h3>{title}</h3></div></header>
                <p>{copy}</p>
                <div>{tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
              </article>
            ))}
          </div>
        </section>

        <section className="public-capability-model" aria-label="LincWerks capability status model">
          <div className="public-section-head"><small>WHAT IS ACTUALLY BUILT</small><h2>The vision stays visible. The status stays honest.</h2><p>LincWerks can show the complete operating model without pretending every capability is already automated or connected. These labels are used across the system to distinguish what works now from what is still being built.</p></div>
          <div className="public-capability-grid">
            {capabilityOrder.map((status) => <article key={status}><CapabilityStatus status={status} /><p>{CAPABILITY_STATUS[status].copy}</p></article>)}
          </div>
        </section>

        <section id="lifecycle" className="public-section public-lifecycle-section">
          <div className="public-section-head"><small>FROM POSSIBILITY TO CONTINUOUS IMPROVEMENT</small><h2>A product lifecycle that keeps going after release.</h2><p>CommandLinc moves work from Idea → Discover → Validate → Decide → BuildLinc → Review & Revise → QA & Security → Release → Operate & Support → Learn & Improve. Break/fix work can return directly to BuildLinc; enhancements can loop back through discovery and validation; genuinely new opportunities can start a fresh idea cycle.</p></div>
          <div className="public-lifecycle-map">
            {lifecycle.map(([label, Icon, tone], index) => (
              <article className={`public-life-node tone-${tone}`} key={label}>
                <span>{index + 1}</span><div className="public-life-icon"><Icon size={17} /></div><b>{label}</b>{index < lifecycle.length - 1 ? <ArrowRight size={13} /> : <PackageCheck size={13} />}
              </article>
            ))}
          </div>
          <div className="public-lifecycle-detail">
            <article><Sparkles size={18} /><div><b>AI where it accelerates the work</b><p>Discovery, analysis, requirements, engineering and recommendations can move faster without pretending the AI is the final decision-maker.</p></div></article>
            <article><ShieldCheck size={18} /><div><b>Human control at consequential gates</b><p>Build decisions, approvals, release decisions, scope changes and other consequential actions remain explicit and auditable.</p></div></article>
            <article><Activity size={18} /><div><b>Clients and support feed the next cycle</b><p>Break/fix, enhancements, maintenance, feedback and usage learning stay connected to the product history and route back to the right lifecycle stage.</p></div></article>
          </div>
        </section>

        <section id="purpose" className="public-purpose">
          <div className="public-purpose-copy"><small>WHY LINCWERKS</small><h2>Build the connective tissue, not another isolated app.</h2><p>LincWerks is being developed around a practical belief: people should be able to move from attention → decision → execution → learning without constantly changing systems or re-explaining the work. That same model can support a sports platform, a storefront, a product-development and manufacturing workflow, and whatever comes next.</p></div>
          <div className="public-purpose-stack">
            <div><Focus size={18} /><span><b>Focus</b><small>Know what needs you now.</small></span></div>
            <div><Link2 size={18} /><span><b>Connect</b><small>Keep work, people and systems linked.</small></span></div>
            <div><Layers3 size={18} /><span><b>Operate</b><small>Move work through visible, governed stages.</small></span></div>
            <div><Compass size={18} /><span><b>Evolve</b><small>Learn from what happened and improve the next cycle.</small></span></div>
          </div>
        </section>

        <section className="public-trust">
          <div><span className="public-trust-icon"><ShieldCheck size={18} /></span><div><b>Public front door. Private operating workspace.</b><p>The ecosystem story is public; operational data and controls stay behind authenticated access.</p></div></div>
          <Link to="/login">Sign in to continue <ArrowRight size={14} /></Link>
        </section>
      </main>

      <footer className="public-footer"><span>LincWerks</span><span>Build · Operate · Connect · Evolve</span></footer>
    </div>
  );
}
